import React, { useState } from "react";
import {
  Row, Col, CardBody, Card, Alert, Container,
  Input, Label, Form, FormFeedback,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";

import logo from "assets/images/logo.svg";
import {
  sendOtpRequest,
  verifyOtpRequest,
  registerRequest,
} from "helpers/auth_client";
import AuthHeader from "components/Common/AuthHeader";

// OTP gönderimi/doğrulaması ve kayıt artık sunucudaki /api/auth uçlarından geçiyor.
const OTP_PURPOSE = "registration";

// Users_Type_ID: 1 = Müşteri, 2 = Sürücü, 3 = ADMIN, 4 = Çekici
// DİKKAT: 3 kayıt ekranından ASLA seçilebilir olmamalı — admin rolüdür.
const USER_TYPES = [
  { id: 1, label: "Müşteri", icon: "bx bxs-user-circle", desc: "Nakliye hizmeti al" },
  { id: 2, label: "Sürücü", icon: "bx bxs-truck", desc: "Nakliye hizmeti ver" },
];

const StepIndicator = ({ step }) => (
  <div className="d-flex align-items-center justify-content-center mb-4">
    {[1, 2, 3].map((s, i) => (
      <React.Fragment key={s}>
        <div
          style={{
            width: 32, height: 32, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700,
            background: step >= s ? "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)" : "#e9ecef",
            color: step >= s ? "#fff" : "#adb5bd",
            transition: "all 0.3s",
            boxShadow: step >= s ? "0 4px 12px rgba(85,110,230,0.4)" : "none",
          }}
        >
          {step > s ? <i className="bx bx-check" style={{ fontSize: 16 }} /> : s}
        </div>
        {i < 2 && (
          <div
            style={{
              flex: 1, height: 2, margin: "0 6px",
              background: step > s ? "linear-gradient(90deg, #556ee6, #6f42c1)" : "#e9ecef",
              transition: "all 0.3s",
            }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

const STEP_LABELS = ["Telefon Doğrulama", "Kod Girişi", "Bilgileriniz"];

const Register = () => {
  document.title = "Kayıt Ol | LojGo";
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [userTypeId, setUserTypeId] = useState(1); // varsayılan: Müşteri
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [formattedPhone, setFormattedPhone] = useState("");
  const [smsLoading, setSmsLoading] = useState(false);

  // OTP doğrulandığında sunucudan alınan tek kullanımlık bilet.
  // Kayıt isteği yalnızca bu biletle kabul edilir.
  const [verificationTicket, setVerificationTicket] = useState("");

  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [submitError, setSubmitError] = useState("");

  // Adım 1: telefon kontrol + SMS
  // Numaranın kayıtlı olup olmadığı kontrolü de sunucuya taşındı; eskiden burada
  // açık OData sorgusuyla yapılıyordu ve kullanıcı tabanını dışarıdan taramaya izin veriyordu.
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    const cleaned = phone.replace(/\D/g, "");
    if (!cleaned || cleaned.length < 10) {
      setPhoneError("Geçerli bir telefon numarası girin (örn: 5321234567)");
      return;
    }
    setSmsLoading(true);
    setPhoneError("");
    try {
      await sendOtpRequest(phone, OTP_PURPOSE);
      setFormattedPhone(phone);
      setStep(2);
    } catch (err) {
      setPhoneError(err.message || "SMS gönderilemedi. Tekrar deneyin.");
    } finally {
      setSmsLoading(false);
    }
  };

  // Adım 2: OTP doğrulama — sonuç sunucudan gelen bilete bağlanır.
  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setOtpError("Lütfen doğrulama kodunu girin.");
      return;
    }
    setOtpLoading(true);
    setOtpError("");
    try {
      const result = await verifyOtpRequest(phone, otp, OTP_PURPOSE);
      if (result?.verificationTicket) {
        setVerificationTicket(result.verificationTicket);
        setStep(3);
      } else {
        setOtpError("Kod doğrulanamadı. Tekrar deneyin.");
      }
    } catch (err) {
      setOtpError(err.message || "Kod hatalı. Tekrar deneyin.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendSms = async () => {
    setSmsLoading(true);
    setOtpError("");
    try {
      await sendOtpRequest(phone, OTP_PURPOSE);
    } catch (err) {
      setOtpError(err.message || "SMS tekrar gönderilemedi.");
    } finally {
      setSmsLoading(false);
    }
  };

  // Adım 3: Bilgileri doldur ve kayıt ol
  const infoValidation = useFormik({
    initialValues: { fullName: "", email: "", password: "", confirmPassword: "" },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Ad Soyad zorunludur"),
      email: Yup.string().email("Geçerli bir e-posta girin").nullable(),
      password: Yup.string().min(6, "Şifre en az 6 karakter olmalıdır").required("Şifre zorunludur"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Şifreler eşleşmiyor")
        .required("Şifre tekrarı zorunludur"),
    }),
    onSubmit: async (values) => {
      setSubmitLoading(true);
      setSubmitError("");
      try {
        // Kayıt artık /api/auth/register üzerinden yapılıyor. Kullanıcı tipi sunucuda
        // beyaz listeye karşı doğrulanır (admin tipi hiçbir koşulda kabul edilmez) ve
        // şifre veritabanına hash'lenerek yazılır.
        await registerRequest({
          nameSurname: values.fullName.trim(),
          phoneNumber: phone.replace(/\D/g, ""),
          email: values.email || "",
          password: values.password,
          userTypeId,
          verificationTicket,
        });

        setSuccessMsg("Kayıt başarılı! Giriş sayfasına yönlendiriliyorsunuz...");
        setTimeout(() => navigate("/login"), 2000);
      } catch (err) {
        setSubmitError(err.message || "Kayıt sırasında hata oluştu. Lütfen tekrar deneyin.");
      } finally {
        setSubmitLoading(false);
      }
    },
  });

  return (
    <React.Fragment>
      <AuthHeader />
      <div
        style={{
          minHeight: "calc(100vh - 80px)",
          background: "linear-gradient(135deg, #f5f7ff 0%, #e8eeff 50%, #f0f4ff 100%)",
          display: "flex", alignItems: "center",
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={9} lg={8} xl={7}>

              {/* Logo + Başlık */}
              <div className="text-center mb-4">
                <Link to="/">
                  <div
                    style={{
                      width: 64, height: 64, borderRadius: 18,
                      background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 8px 24px rgba(85,110,230,0.4)",
                      marginBottom: 12,
                    }}
                  >
                    <img src={logo} alt="LojGo" height="36" style={{ filter: "brightness(0) invert(1)" }} />
                  </div>
                </Link>
                <h3 style={{ fontWeight: 700, color: "#1a1a2e", marginBottom: 4 }}>LojGo'ya Katılın</h3>
                <p style={{ color: "#6c757d", fontSize: 15 }}>
                  {STEP_LABELS[step - 1]}
                </p>
              </div>

              <Card
                style={{
                  border: "none",
                  borderRadius: 20,
                  boxShadow: "0 20px 60px rgba(85,110,230,0.12)",
                  overflow: "hidden",
                }}
              >
                {/* Üst renkli çizgi */}
                <div
                  style={{
                    height: 4,
                    background: "linear-gradient(90deg, #556ee6 0%, #6f42c1 50%, #e83e8c 100%)",
                  }}
                />

                <CardBody style={{ padding: "2rem 2.5rem" }}>
                  <StepIndicator step={step} />

                  {successMsg && (
                    <Alert color="success" className="d-flex align-items-center gap-2">
                      <i className="bx bx-check-circle fs-5" />
                      {successMsg}
                    </Alert>
                  )}

                  {/* ─── ADIM 1: Kullanıcı tipi + telefon ─── */}
                  {step === 1 && (
                    <Form onSubmit={handlePhoneSubmit}>
                      {/* Tip seçimi */}
                      <div className="mb-4">
                        <Label className="form-label fw-semibold text-dark mb-3">Hesap Türünüzü Seçin</Label>
                        <Row>
                          {USER_TYPES.map((type) => (
                            <Col xs={6} key={type.id}>
                              <div
                                onClick={() => setUserTypeId(type.id)}
                                style={{
                                  border: `2px solid ${userTypeId === type.id ? "#556ee6" : "#e9ecef"}`,
                                  borderRadius: 14,
                                  padding: "16px 12px",
                                  textAlign: "center",
                                  cursor: "pointer",
                                  background: userTypeId === type.id
                                    ? "linear-gradient(135deg, #eef1ff 0%, #f0ebff 100%)"
                                    : "#fff",
                                  transition: "all 0.2s",
                                  boxShadow: userTypeId === type.id
                                    ? "0 4px 16px rgba(85,110,230,0.2)"
                                    : "0 2px 8px rgba(0,0,0,0.04)",
                                }}
                              >
                                <div
                                  style={{
                                    width: 48, height: 48, borderRadius: 12, margin: "0 auto 8px",
                                    background: userTypeId === type.id
                                      ? "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)"
                                      : "#f1f3fa",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                  }}
                                >
                                  <i
                                    className={type.icon}
                                    style={{
                                      fontSize: 22,
                                      color: userTypeId === type.id ? "#fff" : "#8592c0",
                                    }}
                                  />
                                </div>
                                <div style={{ fontWeight: 700, fontSize: 14, color: userTypeId === type.id ? "#556ee6" : "#495057" }}>
                                  {type.label}
                                </div>
                                <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 2 }}>
                                  {type.desc}
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>

                      {/* Telefon */}
                      <div className="mb-4">
                        <Label className="form-label fw-semibold text-dark">Telefon Numarası</Label>
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                              border: "none", color: "#fff", borderRadius: "10px 0 0 10px",
                            }}
                          >
                            <i className="bx bx-phone" style={{ fontSize: 18 }} />
                          </span>
                          <Input
                            type="tel"
                            placeholder="5321234567"
                            value={phone}
                            onChange={(e) => { setPhone(e.target.value); setPhoneError(""); }}
                            style={{
                              borderLeft: "none", borderRadius: "0 10px 10px 0",
                              padding: "10px 14px", fontSize: 15,
                            }}
                            invalid={!!phoneError}
                          />
                          {phoneError && <FormFeedback>{phoneError}</FormFeedback>}
                        </div>
                        <small className="text-muted mt-1 d-block">
                          <i className="bx bx-info-circle me-1" />
                          Türkiye numaranızı girin. +90 otomatik eklenecektir.
                        </small>
                      </div>

                      <button
                        type="submit"
                        disabled={smsLoading}
                        style={{
                          width: "100%", padding: "12px",
                          background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                          border: "none", borderRadius: 12, color: "#fff",
                          fontWeight: 700, fontSize: 15,
                          boxShadow: "0 8px 20px rgba(85,110,230,0.4)",
                          transition: "all 0.2s",
                          opacity: smsLoading ? 0.8 : 1,
                        }}
                      >
                        {smsLoading ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Kontrol ediliyor...</>
                        ) : (
                          <><i className="bx bx-send me-2" />Doğrulama Kodu Gönder</>
                        )}
                      </button>
                    </Form>
                  )}

                  {/* ─── ADIM 2: OTP ─── */}
                  {step === 2 && (
                    <div>
                      <div className="text-center mb-4">
                        <div
                          style={{
                            width: 80, height: 80, borderRadius: 20,
                            background: "linear-gradient(135deg, #eef1ff 0%, #f0ebff 100%)",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            marginBottom: 16, border: "2px solid #d6dbff",
                          }}
                        >
                          <i className="bx bx-message-detail" style={{ fontSize: 36, color: "#556ee6" }} />
                        </div>
                        <h5 style={{ fontWeight: 700, color: "#1a1a2e" }}>SMS Gönderildi</h5>
                        <p className="text-muted" style={{ fontSize: 14 }}>
                          <strong style={{ color: "#556ee6" }}>{formattedPhone}</strong> numarasına
                          6 haneli doğrulama kodu gönderdik.
                        </p>
                      </div>

                      {otpError && (
                        <Alert color="danger" className="d-flex align-items-center gap-2">
                          <i className="bx bx-x-circle fs-5" />
                          {otpError}
                        </Alert>
                      )}

                      <div className="mb-4">
                        <Label className="form-label fw-semibold text-dark text-center d-block">
                          Doğrulama Kodu
                        </Label>
                        <Input
                          type="text"
                          placeholder="• • • • • •"
                          maxLength={6}
                          value={otp}
                          onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "")); setOtpError(""); }}
                          style={{
                            textAlign: "center", fontSize: 28, fontWeight: 700,
                            letterSpacing: 12, borderRadius: 12, padding: "14px",
                            border: "2px solid #dee2e6", color: "#556ee6",
                          }}
                        />
                      </div>

                      <button
                        onClick={handleVerifyOtp}
                        disabled={otpLoading || otp.length < 6}
                        style={{
                          width: "100%", padding: "12px",
                          background: otp.length === 6
                            ? "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)"
                            : "#e9ecef",
                          border: "none", borderRadius: 12,
                          color: otp.length === 6 ? "#fff" : "#adb5bd",
                          fontWeight: 700, fontSize: 15,
                          boxShadow: otp.length === 6 ? "0 8px 20px rgba(85,110,230,0.4)" : "none",
                          transition: "all 0.2s",
                        }}
                      >
                        {otpLoading ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Doğrulanıyor...</>
                        ) : (
                          <><i className="bx bx-check-shield me-2" />Kodu Doğrula</>
                        )}
                      </button>

                      <div className="text-center mt-3 d-flex justify-content-center gap-3">
                        <button
                          className="btn btn-link p-0"
                          style={{ fontSize: 13, color: "#6c757d", textDecoration: "none" }}
                          onClick={handleResendSms}
                          disabled={smsLoading}
                        >
                          <i className="bx bx-refresh me-1" />
                          {smsLoading ? "Gönderiliyor..." : "Kodu tekrar gönder"}
                        </button>
                        <span style={{ color: "#dee2e6" }}>|</span>
                        <button
                          className="btn btn-link p-0"
                          style={{ fontSize: 13, color: "#6c757d", textDecoration: "none" }}
                          onClick={() => { setStep(1); setOtp(""); setOtpError(""); }}
                        >
                          <i className="bx bx-arrow-back me-1" />
                          Geri dön
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ─── ADIM 3: Bilgiler ─── */}
                  {step === 3 && (
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        infoValidation.handleSubmit();
                      }}
                    >
                      {submitError && (
                        <Alert color="danger" className="d-flex align-items-center gap-2">
                          <i className="bx bx-x-circle fs-5" />
                          {submitError}
                        </Alert>
                      )}

                      <div
                        style={{
                          background: "linear-gradient(135deg, #eef1ff 0%, #f0ebff 100%)",
                          borderRadius: 12, padding: "10px 14px", marginBottom: 20,
                          display: "flex", alignItems: "center", gap: 10,
                          border: "1px solid #d6dbff",
                        }}
                      >
                        <i className="bx bx-check-circle" style={{ fontSize: 20, color: "#556ee6" }} />
                        <div>
                          <div style={{ fontSize: 12, color: "#6c757d" }}>Doğrulanan numara</div>
                          <div style={{ fontWeight: 700, color: "#556ee6", fontSize: 14 }}>{formattedPhone}</div>
                        </div>
                      </div>

                      <Row>
                        <Col md={12}>
                          <div className="mb-3">
                            <Label className="form-label fw-semibold text-dark">Ad Soyad</Label>
                            <div className="input-group">
                              <span
                                className="input-group-text"
                                style={{
                                  background: "#f8f9ff", border: "1.5px solid #dee2e6",
                                  borderRight: "none", borderRadius: "10px 0 0 10px",
                                }}
                              >
                                <i className="bx bx-user" style={{ color: "#8592c0" }} />
                              </span>
                              <Input
                                name="fullName"
                                type="text"
                                placeholder="Adınızı ve soyadınızı girin"
                                onChange={infoValidation.handleChange}
                                onBlur={infoValidation.handleBlur}
                                value={infoValidation.values.fullName}
                                invalid={!!(infoValidation.touched.fullName && infoValidation.errors.fullName)}
                                style={{ borderLeft: "none", borderRadius: "0 10px 10px 0" }}
                              />
                              {infoValidation.touched.fullName && infoValidation.errors.fullName && (
                                <FormFeedback>{infoValidation.errors.fullName}</FormFeedback>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <div className="mb-3">
                        <Label className="form-label fw-semibold text-dark">
                          E-posta <span style={{ color: "#adb5bd", fontWeight: 400 }}>(isteğe bağlı)</span>
                        </Label>
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              background: "#f8f9ff", border: "1.5px solid #dee2e6",
                              borderRight: "none", borderRadius: "10px 0 0 10px",
                            }}
                          >
                            <i className="bx bx-envelope" style={{ color: "#8592c0" }} />
                          </span>
                          <Input
                            name="email"
                            type="email"
                            placeholder="E-posta adresinizi girin"
                            onChange={infoValidation.handleChange}
                            onBlur={infoValidation.handleBlur}
                            value={infoValidation.values.email}
                            invalid={!!(infoValidation.touched.email && infoValidation.errors.email)}
                            style={{ borderLeft: "none", borderRadius: "0 10px 10px 0" }}
                          />
                          {infoValidation.touched.email && infoValidation.errors.email && (
                            <FormFeedback>{infoValidation.errors.email}</FormFeedback>
                          )}
                        </div>
                      </div>

                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label className="form-label fw-semibold text-dark">Şifre</Label>
                            <div className="input-group">
                              <span
                                className="input-group-text"
                                style={{
                                  background: "#f8f9ff", border: "1.5px solid #dee2e6",
                                  borderRight: "none", borderRadius: "10px 0 0 10px",
                                }}
                              >
                                <i className="bx bx-lock-alt" style={{ color: "#8592c0" }} />
                              </span>
                              <Input
                                name="password"
                                type="password"
                                placeholder="En az 6 karakter"
                                onChange={infoValidation.handleChange}
                                onBlur={infoValidation.handleBlur}
                                value={infoValidation.values.password}
                                invalid={!!(infoValidation.touched.password && infoValidation.errors.password)}
                                style={{ borderLeft: "none", borderRadius: "0 10px 10px 0" }}
                              />
                              {infoValidation.touched.password && infoValidation.errors.password && (
                                <FormFeedback>{infoValidation.errors.password}</FormFeedback>
                              )}
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label className="form-label fw-semibold text-dark">Şifre Tekrar</Label>
                            <div className="input-group">
                              <span
                                className="input-group-text"
                                style={{
                                  background: "#f8f9ff", border: "1.5px solid #dee2e6",
                                  borderRight: "none", borderRadius: "10px 0 0 10px",
                                }}
                              >
                                <i className="bx bx-lock" style={{ color: "#8592c0" }} />
                              </span>
                              <Input
                                name="confirmPassword"
                                type="password"
                                placeholder="Tekrar girin"
                                onChange={infoValidation.handleChange}
                                onBlur={infoValidation.handleBlur}
                                value={infoValidation.values.confirmPassword}
                                invalid={!!(infoValidation.touched.confirmPassword && infoValidation.errors.confirmPassword)}
                                style={{ borderLeft: "none", borderRadius: "0 10px 10px 0" }}
                              />
                              {infoValidation.touched.confirmPassword && infoValidation.errors.confirmPassword && (
                                <FormFeedback>{infoValidation.errors.confirmPassword}</FormFeedback>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <button
                        type="submit"
                        disabled={submitLoading}
                        style={{
                          width: "100%", padding: "12px", marginTop: 8,
                          background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)",
                          border: "none", borderRadius: 12, color: "#fff",
                          fontWeight: 700, fontSize: 15,
                          boxShadow: "0 8px 20px rgba(85,110,230,0.4)",
                        }}
                      >
                        {submitLoading ? (
                          <><span className="spinner-border spinner-border-sm me-2" />Kaydediliyor...</>
                        ) : (
                          <><i className="bx bx-user-plus me-2" />Hesabı Oluştur</>
                        )}
                      </button>
                    </Form>
                  )}
                </CardBody>
              </Card>

              <div className="text-center mt-4">
                <p style={{ color: "#6c757d", fontSize: 14 }}>
                  Zaten hesabınız var mı?{" "}
                  <Link to="/login" style={{ color: "#556ee6", fontWeight: 700, textDecoration: "none" }}>
                    Giriş Yap
                  </Link>
                </p>
                <p style={{ color: "#adb5bd", fontSize: 12 }}>
                  &copy; {new Date().getFullYear()} LojGo. Tüm hakları saklıdır.
                </p>
              </div>

            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Register;
